import { getRequestGeneric, putRequestGeneric } from "@/services/backendApiGeneric";

/*
  Benachrichtigungs-Einstellungen einer Akte (Feedback 15.09.2026, Teil 2).

  Migration 0116 kannte nur den fallweiten Schalter, und der ließ sich
  ausschließlich beim Anlegen setzen. Aus dem Feedback:

    - "Deaktivier ich das dann für alle? Oder geht das auch so, dass ich es nur
      für das AC Benz deaktiviere?"
    - "Deaktivierung später wieder aktivieren? Wo finde ich solche
      Einstellungen für den FALL?"
    - "Ich muss das aber auch bei den einzelnen Beteiligten ein- und
      ausschalten können!"

  Migration 0170 ergänzt die Stummschaltung je Beteiligtem. Beide Ebenen sind
  über diese Endpunkte jederzeit änderbar.
*/

// Stabiler Schlüssel eines Beteiligten. Entspricht den Statuswerten der
// Beteiligten-Liste; der Backend-Guard prüft zusätzlich die Adresse.
export type ParticipantMuteType = "department" | "external" | "invitation" | "client" | "user";

export interface ParticipantMute {
  participantType: ParticipantMuteType;
  idParticipant: number;
  email?: string;
  mutedAt?: string;
  idMutedBy?: number;
}

/** Wer eine Akte ohne Sachbearbeiter erreicht (Kundenfeedback 02.10.26, VI.). */
export interface FallbackRecipientInfo {
  idUser?: number;
  name: string;
  email: string;
  /** standard = Standard-Empfänger, postfach = Sammel-Postfach, zentrale = Administratoren/Unternehmensleiter/„Zentrale" */
  reason: "standard" | "postfach" | "zentrale" | string;
}

export interface CaseNotificationSettings {
  /** true = niemand erhält Benachrichtigungen zu diesem Fall. */
  caseDisabled: boolean;
  /** Einzeln stummgeschaltete Beteiligte. */
  participantMutes: ParticipantMute[];
  /**
   * Adressen der Versicherungs-Beteiligten dieses Falls. Sie erhalten
   * systemseitig nie Benachrichtigungen und lassen sich nicht freischalten.
   * Dient nur der Anzeige — durchgesetzt wird die Regel im Versandpfad.
   */
  insuranceBlockedEmails?: string[];
  /** Die Akte hat in der eigenen Organisation einen aktiven Sachbearbeiter. */
  hasCaseHandler?: boolean;
  /** Die Akte hat in der eigenen Organisation mindestens einen Sachbearbeiter, stumme mitgezählt. */
  hasAnyCaseHandler?: boolean;
  /** Wer die Akte erreicht, solange sie keinen Sachbearbeiter hat. Nur Anzeige. */
  fallbackRecipients?: FallbackRecipientInfo[];
  /** Die eigene Zentrale bleibt neben den Sachbearbeitern bewusst zuständig. */
  centralKept?: boolean;
  /** Die eigene Zentrale wurde abgewählt – wirkt, solange ein (stummer) Sachbearbeiter eingetragen ist. */
  centralOff?: boolean;
  /** Die Zentrale ist derzeit zuständig – beibehalten oder mangels aktivem Sachbearbeiter. */
  centralResponsible?: boolean;
}

/**
 * Rolle, die eine Versicherung kennzeichnet.
 *
 * Versicherungen sind Beteiligte einer Akte, haben aber bewusst keinen Zugang
 * zu KARS und erhalten nie Benachrichtigungen. Das ist eine Systemregel, keine
 * Einstellung: Sie steht über dem Fall-Schalter und über der Stummschaltung je
 * Beteiligtem und lässt sich in der Oberfläche nicht abschalten.
 */
export const ROLE_INSURANCE = "Versicherung";

/**
 * Meldet, ob ein Beteiligter eine Versicherung ist.
 *
 * Der Abgleich ist bewusst exakt und nie eine Teilzeichenketten-Suche: Die
 * Rolle "Versicherungsmakler" beginnt mit "Versicherung", ist aber ein
 * regulärer Beteiligter mit Portalzugang und muss weiter benachrichtigt
 * werden.
 */
export function isInsuranceParticipant(participant: { roles?: unknown }): boolean {
  const roles = Array.isArray(participant?.roles) ? participant.roles : [];
  return roles.some((role) => typeof role === "string" && role.trim() === ROLE_INSURANCE);
}

export function loadCaseNotificationSettings(idCase: number | string): Promise<CaseNotificationSettings> {
  return getRequestGeneric(`/protected/cases/${idCase}/notifications`);
}

/** Schaltet die fallweiten Benachrichtigungen in beide Richtungen. */
export function setCaseNotificationsDisabled(
  idCase: number | string,
  caseDisabled: boolean,
): Promise<{ caseDisabled: boolean }> {
  return putRequestGeneric(`/protected/cases/${idCase}/notifications`, { caseDisabled }, true);
}

/**
 * Schaltet einen einzelnen Beteiligten stumm (muted = true) oder hebt die
 * Stummschaltung auf. Liefert die vollständige, aktualisierte Liste zurück.
 *
 * email wird als Snapshot mitgesendet: Der Versandpfad kennt in vielen Fällen
 * nur die Empfängeradresse, nicht die Beteiligten-ID.
 */
export function setParticipantNotificationMute(
  idCase: number | string,
  participantType: ParticipantMuteType,
  idParticipant: number,
  options: { muted: boolean; email?: string; name?: string },
): Promise<ParticipantMute[]> {
  return putRequestGeneric(
    `/protected/cases/${idCase}/notifications/participants/${participantType}/${idParticipant}`,
    {
      muted: options.muted,
      email: options.email || "",
      name: options.name || "",
    },
    true,
  );
}

/**
 * Übersetzt einen Eintrag der Beteiligten-Liste in den Mute-Schlüssel.
 * Mandanten kommen mit type = "mandant" und tragen denselben Statuswert wie
 * Abteilungen ("internal") — ohne diese Unterscheidung würden sich Mandanten-
 * und Abteilungs-IDs überlagern.
 */
export function participantMuteKey(
  participant: { type?: string; status?: string; id: number },
): { participantType: ParticipantMuteType; idParticipant: number } | null {
  if (!participant || !Number.isFinite(Number(participant.id))) return null;

  const idParticipant = Number(participant.id);
  // Die eigene Kanzlei steht mit ID 0 in der Liste (Platzhalter ohne
  // Abteilung). Sie hat keinen eigenen Kanal; eine Stummschaltung darauf
  // hätte im Versand keine Wirkung (KArs 23.09.2026 Teil 2, V).
  if (idParticipant <= 0) return null;

  if (participant.type === "mandant") {
    return { participantType: "client", idParticipant };
  }
  // Sachbearbeiter der Akte (tbl_cases_team) stehen als Person in der Liste,
  // ebenfalls mit Status "internal" — ihre ID ist aber eine Nutzer-ID. Ohne
  // diese Unterscheidung wurden sie als Abteilung gemeldet und der Server
  // lehnte die Stummschaltung mit 409 ab (Kundenfeedback 18.09.2026).
  if (participant.type === "sachbearbeiter") {
    return { participantType: "user", idParticipant };
  }
  switch (participant.status) {
    case "internal":
      return { participantType: "department", idParticipant };
    case "external":
      return { participantType: "external", idParticipant };
    case "pending":
      return { participantType: "invitation", idParticipant };
    default:
      // "unregistered" u. Ä. haben keinen Portalzugang und damit keinen
      // eigenen Benachrichtigungskanal.
      return null;
  }
}

/**
 * Zählt die stummgeschalteten Beteiligten, die in der Akte tatsächlich noch
 * vorkommen — dieselbe Menge, die der Einstellungs-Dialog als Zeilen zeigt.
 *
 * Vorher zählte das Banner jede gespeicherte Stummschaltung, auch die von
 * entfernten Beteiligten. „5 Beteiligte stumm" passte dann nicht zu dem, was
 * der Dialog anzeigte (KArs 23.09.2026 Teil 2, V).
 */
export function countMutedParticipants(
  participants: Array<{ type?: string; status?: string; id: number; removedAt?: unknown }>,
  mutes: ParticipantMute[],
): number {
  const muted = new Set((mutes || []).map((m) => `${m.participantType}:${m.idParticipant}`));
  const seen = new Set<string>();
  for (const p of participants || []) {
    if (!p || p.removedAt) continue;
    const key = participantMuteKey(p);
    if (!key) continue;
    const lookup = `${key.participantType}:${key.idParticipant}`;
    if (muted.has(lookup)) seen.add(lookup);
  }
  return seen.size;
}

/** Entscheidung der eigenen Organisation zu ihrer Zentrale in einer Akte. */
export type CentralMode = "auto" | "kept" | "off";

/**
 * Setzt die Zuständigkeit der Zentrale in der Akte:
 *  - "kept": bleibt neben aktiven Sachbearbeitern zuständig,
 *  - "auto": zuständig nur, solange kein aktiver Sachbearbeiter eingetragen ist
 *    (Mail Kanzlei Polz, 22.09./06.10.2026 – „einer muss IMMER ausgewählt sein"),
 *  - "off": abgewählt – die (stummen) Sachbearbeiter beobachten die Akte nur
 *    (Kundenfeedback 09.10.2026, Akte 1808/26). Ohne Sachbearbeiter lehnt das
 *    Backend die Abwahl ab (409).
 */
export function setCentralResponsibility(
  idCase: number | string,
  mode: CentralMode,
): Promise<{ centralKept: boolean; centralOff: boolean; centralResponsible: boolean }> {
  return putRequestGeneric(`/protected/cases/${idCase}/notifications/central`, { mode }, true);
}

/**
 * Rückmeldung nach dem Umschalten der Zentrale – gleicher Text im
 * Sachbearbeiter-Dialog und in den Benachrichtigungs-Einstellungen.
 */
export function centralModeMessage(mode: CentralMode, hasActiveHandler: boolean): string {
  switch (mode) {
    case "kept":
      return "Die Zentrale bleibt für diesen Fall zuständig.";
    case "off":
      return "Zentrale abgewählt – die stummen Sachbearbeiter beobachten die Akte nur; keine Aufgaben, Wiedervorlagen und E-Mail-Eingänge bei der Zentrale.";
    default:
      return hasActiveHandler
        ? "Zentrale abgewählt – Aufgaben und Wiedervorlagen gehen an die aktiven Sachbearbeiter."
        : "Die Zentrale ist wieder zuständig – Aufgaben, Wiedervorlagen und E-Mail-Eingänge gehen an die Zentrale.";
  }
}

/**
 * Meldet, ob ein Sachbearbeiter (Nutzer-ID) in dieser Akte stumm ist. Stumme
 * Sachbearbeiter beobachten nur: keine E-Mails, Aufgaben und Wiedervorlagen
 * (Mail Kanzlei Polz, 06.10.2026).
 */
export function isUserMuted(mutes: ParticipantMute[], idUser: number): boolean {
  return (mutes || []).some((m) => m.participantType === "user" && Number(m.idParticipant) === Number(idUser));
}
